/**
 * Hooks del Vault (F3a). Patrón de useInternalTasks: un hook por módulo,
 * mutations con invalidate + toast.
 *
 * La sesión de desbloqueo (token de 15 min) vive en memoria en lib/vault-session.ts;
 * acá se expone como store de React con useSyncExternalStore.
 */
import { useEffect, useState, useSyncExternalStore } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { trpc } from '@/lib/trpc';
import i18n from '@/i18n/i18n';
import {
  clearVaultUnlockToken,
  notifyMfaRequired,
  getVaultUnlockExpiry,
  getVaultUnlockToken,
  setVaultUnlockToken,
  subscribeVaultSession,
} from '@/lib/vault-session';

const ACCESS_OPTS = { retry: false, staleTime: 5 * 60_000 } as const;

/** El FORBIDDEN que manda el backend cuando falta el token de desbloqueo. */
export const isVaultLockedError = (e: { message?: string } | null | undefined) => e?.message === 'VAULT_LOCKED';
export const isMfaRequiredError = (e: { message?: string } | null | undefined) => e?.message === 'MFA_REQUIRED';

/**
 * Mensaje para mostrar. Los dos códigos del gate del Vault se traducen acá para
 * TODOS los sitios (auditoría 28/09: 20 lugares podían mostrar VAULT_LOCKED /
 * MFA_REQUIRED crudos). Usa la instancia de i18n porque casi siempre corre en un
 * onError fuera del render (mismo criterio que lib/toast-error.ts).
 */
export const errMsg = (e: { message?: string } | null | undefined) => {
  if (isVaultLockedError(e)) return i18n.t('vault.errors.locked');
  if (isMfaRequiredError(e)) return i18n.t('vault.errors.mfa_required');
  return e?.message ?? i18n.t('vault.errors.unexpected');
};

export function useVaultAccess() {
  return trpc.vault.getMyAccess.useQuery(undefined, ACCESS_OPTS);
}

/** Estado de la sesión de desbloqueo (memoria). Re-renderiza al bloquear/desbloquear. */
export function useVaultSession() {
  const token = useSyncExternalStore(subscribeVaultSession, getVaultUnlockToken, () => null);
  const expiresAt = useSyncExternalStore(subscribeVaultSession, getVaultUnlockExpiry, () => null);
  // Reloj para que `unlocked` se apague solo cuando vence el token.
  const [, tick] = useState(0);
  useEffect(() => {
    if (!expiresAt) return;
    const ms = Math.max(expiresAt - Date.now(), 0) + 50;
    const id = window.setTimeout(() => tick((n) => n + 1), ms);
    return () => window.clearTimeout(id);
  }, [expiresAt]);
  return {
    unlocked: !!token,
    expiresAt,
    lock: clearVaultUnlockToken,
  };
}

export function useVaultMutations() {
  const { t } = useTranslation();
  const utils = trpc.useUtils();
  const invalidateAll = () => {
    utils.vault.getMyAccess.invalidate();
    utils.vault.listSecrets.invalidate();
  };

  const onToken = (data: { unlockToken: string; expiresInMinutes: number }) => {
    setVaultUnlockToken(data.unlockToken, data.expiresInMinutes);
  };

  const setupPassphrase = trpc.vault.setupPassphrase.useMutation({
    onSuccess: (data) => {
      onToken(data);
      invalidateAll();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const unlock = trpc.vault.unlock.useMutation({
    onSuccess: (data) => {
      onToken(data);
      utils.vault.getMyAccess.invalidate();
    },
  });

  const changePassphrase = trpc.vault.changePassphrase.useMutation({
    onSuccess: (data) => {
      onToken(data);
      toast.success(t('vault.toast.passphrase_updated', 'Passphrase actualizada'));
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const createSecret = trpc.vault.createSecret.useMutation({
    onSuccess: () => {
      utils.vault.listSecrets.invalidate();
      toast.success(t('vault.toast.secret_created', 'Credencial creada'));
    },
    onError: (e) => {
      if (!isVaultLockedError(e)) toast.error(errMsg(e));
    },
  });

  const revealSecret = trpc.vault.revealSecret.useMutation({
    onSuccess: (_d, vars) => utils.vault.getSecret.invalidate({ secretId: vars.secretId }),
  });
  const getTotp = trpc.vault.getTotp.useMutation();

  return { setupPassphrase, unlock, changePassphrase, createSecret, revealSecret, getTotp };
}

/**
 * Corre una acción que exige el Vault desbloqueado (revelar, rotar, TOTP,
 * link, crear). Si el backend contesta VAULT_LOCKED aunque el token local
 * parecía vigente (lo invalidó un cambio o reset de passphrase desde otra
 * pestaña), se descarta el token, se vuelve a pedir la passphrase y se
 * reintenta UNA vez. Devuelve null si la persona canceló el desbloqueo.
 * Antes solo Revelar hacía esto; los otros cinco flujos mostraban
 * "VAULT_LOCKED" crudo (auditoría 28/09).
 */
export async function withUnlockRetry<T>(ensureUnlocked: () => Promise<boolean>, run: () => Promise<T>): Promise<T | null> {
  if (!(await ensureUnlocked())) return null;
  try {
    return await run();
  } catch (e) {
    if (isMfaRequiredError(e as { message?: string })) {
      notifyMfaRequired(); // la ruta vuelve a gatear y muestra la pantalla de 2SV
      throw e;
    }
    if (!isVaultLockedError(e as { message?: string })) throw e;
    clearVaultUnlockToken();
    if (!(await ensureUnlocked())) return null;
    return run();
  }
}

/** 28/10/2026: vive en hooks/useCopy.ts (compartido); se re-exporta para no tocar a los consumidores del Vault. */
export { useCopy } from './useCopy';
